/**
 * Australian-specific constants for forms and dropdowns
 */

export const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
] as const;

export const LEGAL_PERSONAL_REPRESENTATIVE_ROLES = [
  { value: 'executor', label: 'Executor' },
  {
    value: 'applicant_for_letters_of_administration',
    label: 'Applicant for Letters of Administration'
  }
] as const;

/**
 * Reasons a named executor (LPR) is non-proving. Proving vs non-proving is an
 * Australia-wide common-law concept: where a will names multiple co-executors,
 * the proving executors apply for the grant and any named executor not part of
 * that application is non-proving. Every non-proving executor is non-proving
 * for exactly one of these reasons; "leave reserved" is only one of them, not
 * a synonym for non-proving. See LF-5372 (mirrors the backend NonProvingReason
 * enum). `description` is surfaced as subtext when the reason picker is open.
 */
export const NON_PROVING_REASONS = [
  {
    value: 'leave_reserved',
    label: 'Leave reserved',
    description:
      'Steps back from acting now but keeps the right to prove the will later.'
  },
  {
    value: 'renunciation',
    label: 'Renunciation',
    description:
      'Permanently gives up the right to act; cannot step back in later.'
  },
  {
    value: 'predeceased',
    label: 'Predeceased',
    description: 'Died before the will-maker.'
  },
  {
    value: 'incapacity',
    label: 'Incapacity',
    description: 'Has lost the mental capacity to manage the estate.'
  },
  {
    value: 'passed_over',
    label: 'Passed over',
    description:
      'Bypassed by the court before a grant is made (conflict, unfitness, or cannot be located).'
  }
] as const;

export type NonProvingReason = (typeof NON_PROVING_REASONS)[number]['value'];

/** value → short pill/display label for a non-proving reason. */
export const NON_PROVING_REASON_LABELS = Object.fromEntries(
  NON_PROVING_REASONS.map((r) => [r.value, r.label])
) as Record<NonProvingReason, string>;

/**
 * Country data is NOT Australian-specific and lives in `./countries.ts` — the canonical
 * ISO 3166 name list, its `{ value, label }` option form, and `DEFAULT_COUNTRY`. Re-exported
 * here only so existing `@/lib/constants` importers keep working; import from
 * `./countries` directly in new code.
 */
export { DEFAULT_COUNTRY, COUNTRY_NAMES, COUNTRY_OPTIONS } from './countries';
export type { CountryName } from './countries';
